import { useState, useMemo, useEffect } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSearch,
  faTimes,
  faChevronLeft,
  faChevronRight,
  faChevronUp,
  faChevronDown,
  faCircle,
  faStar,
  faCheckSquare,
  faLayerGroup,
} from '@fortawesome/free-solid-svg-icons'
import { useAccountContext } from '../contexts/AccountContext'
import { useAppContext } from '../contexts/AppContext'
import { useCompareToggle } from '../hooks/useCompareToggle'
import { FEATURE_FLAGS } from '../utils/featureFlags'
import { MAX_COMPARE_DEVICES } from '../common/constants'
import { Switch } from './Switch'
import { DeviceInfo } from '../common/enums'
import { EmptyState } from '../shared/components/ui'
import { DeviceStatus } from '../utils/deviceStatus'
import { DeviceStatusIcon } from './DeviceStatusIcon'
import { useDeviceStatuses } from '../hooks/useDeviceStatuses'
import { useDevicePriorities, organizeDevicesByPriorityAndStatus } from '../hooks/useDevicePriorities'
import { useDeviceBatteryStates } from '../hooks/useDeviceBatteryStates'

// View mode types for the device list
type ViewMode = 'status' | 'name' | 'site' | 'account'
type SortDirection = 'asc' | 'desc'

// Export sidebar constants for use in layout
export const SIDEBAR_MIN_WIDTH = 300
export const SIDEBAR_MAX_WIDTH = 500
export const SIDEBAR_DEFAULT_WIDTH = 400
export const SIDEBAR_WIDTH_KEY = 'sidebar_width'
export const SIDEBAR_COLLAPSED_WIDTH = 56 // 14 * 4 = 56px (w-14)
export const SIDEBAR_VIEW_MODE_KEY = 'sidebar_view_mode'

interface DeviceListSidebarProps {
  isCollapsed: boolean
  onToggleCollapse: () => void
  width: number
  onWidthChange: (width: number) => void
}

export function DeviceListSidebar({ isCollapsed, onToggleCollapse, width, onWidthChange }: DeviceListSidebarProps) {
  const {
    devices,
    selectedDevice,
    selectDeviceLocally,
    isLoading,
    isAdminAllDevices,
    isPaginating,
    loadedCount,
    totalCount,
  } = useAccountContext()
  const {
    compareMode,
    selectedDevicesForComparison,
    toggleDeviceForComparison,
  } = useAppContext()
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('q') || '')

  // Persist the search query in the URL (?q=…) so a refresh/reload preserves
  // what the admin typed and the URL is shareable. Use replace to avoid
  // polluting browser history with a navigation per keystroke.
  useEffect(() => {
    const current = searchParams.get('q') || ''
    if (current === searchQuery) return
    const next = new URLSearchParams(searchParams)
    if (searchQuery) next.set('q', searchQuery)
    else next.delete('q')
    setSearchParams(next, { replace: true })
  }, [searchQuery, searchParams, setSearchParams])
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem(SIDEBAR_VIEW_MODE_KEY)
    return (saved === 'status' || saved === 'name' || saved === 'site' || saved === 'account') ? saved : 'name'
  })
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const navigate = useNavigate()
  const location = useLocation()

  // Compare mode: this switch lives next to device selection (where you actually
  // pick the devices to compare). Only relevant on the charts/compare views.
  const toggleCompareMode = useCompareToggle()
  const showCompareToggle =
    FEATURE_FLAGS.COMPARE_SENSORS &&
    (location.pathname === '/charts' || location.pathname === '/compare')
  const handleToggleCompareMode = () => {
    const fromParam = searchParams.get('from')
    const toParam = searchParams.get('to')
    toggleCompareMode({
      sigfoxId: selectedDevice || undefined,
      timeRange: {
        from: fromParam ? Number(fromParam) * 1000 : undefined,
        to: toParam ? Number(toParam) * 1000 : undefined,
      },
    })
  }

  // Sidebar resize state
  const [isResizing, setIsResizing] = useState(false)

  // Handle sidebar resize drag
  useEffect(() => {
    if (!isResizing) return

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, e.clientX))
      onWidthChange(newWidth)
    }

    const handleMouseUp = () => {
      setIsResizing(false)
      localStorage.setItem(SIDEBAR_WIDTH_KEY, width.toString())
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isResizing, width, onWidthChange])

  const handleResizeMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
  }

  // Persist view mode to localStorage
  useEffect(() => {
    localStorage.setItem(SIDEBAR_VIEW_MODE_KEY, viewMode)
  }, [viewMode])

  // Admins only have "By Status" and "By Account". If a stale "name"/"site"
  // preference is loaded, snap to "status" so the view stays valid.
  useEffect(() => {
    if (isAdminAllDevices && (viewMode === 'name' || viewMode === 'site')) {
      setViewMode('status')
    }
  }, [isAdminAllDevices, viewMode])

  // By Account groups start collapsed by default; admin clicks a header to expand.
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(new Set())

  // When a device is selected (e.g. via ?sigfoxId= in the URL), auto-expand
  // the account group it belongs to so the row is actually visible without
  // the admin having to hunt for and click open the right group.
  useEffect(() => {
    if (!selectedDevice) return
    const device = (devices as any)[selectedDevice]
    if (!device) return
    const raw = (device.accountName || '').trim()
    const groupKey = !raw || raw.toUpperCase() === 'UNKNOWN LOCATION' ? 'UNASSIGNED' : raw
    setExpandedAccounts((prev) => {
      if (prev.has(groupKey)) return prev
      const next = new Set(prev)
      next.add(groupKey)
      return next
    })
  }, [selectedDevice, devices])
  // Within each account group, secondary sort key. Online + priority always
  // bubble to the top; this controls how the rest of the rows order.
  type AccountSortKey = 'name' | 'site' | 'favorites'
  const [accountSortKey, setAccountSortKey] = useState<AccountSortKey>('name')
  const toggleAccountExpanded = (name: string) => {
    setExpandedAccounts((prev) => {
      const next = new Set(prev)
      if (next.has(name)) next.delete(name)
      else next.add(name)
      return next
    })
  }

  // Convert devices object to array for filtering
  const devicesArray = useMemo(() => {
    return Object.values(devices)
  }, [devices])

  // Fetch device statuses using latest readings from backend
  const { deviceStatuses } = useDeviceStatuses(devicesArray)

  // Fetch battery alert states
  const { lowBatteryDevices } = useDeviceBatteryStates()

  // Use device priorities hook
  const { enhanceDevicesWithPriority, toggleDevicePriority, priorityDeviceIds } = useDevicePriorities()

  // Filter devices by search query
  const filteredDevices = useMemo(() => {
    if (!searchQuery.trim()) return devicesArray

    const query = searchQuery.toLowerCase()
    return devicesArray.filter(
      (device) =>
        device.deviceName?.toLowerCase().includes(query) ||
        device.siteName?.toLowerCase().includes(query) ||
        device.sigfoxId?.toLowerCase().includes(query) ||
        device.wisolId?.toLowerCase().includes(query) ||
        device.siteCode?.toString().toLowerCase().includes(query) ||
        device.accountName?.toLowerCase().includes(query)
    )
  }, [devicesArray, searchQuery])

  // Enhance devices with priority information and organize by priority and status
  const organizedDevices = useMemo(() => {
    const enhancedDevices = enhanceDevicesWithPriority(filteredDevices)
    return organizeDevicesByPriorityAndStatus(enhancedDevices, deviceStatuses)
  }, [filteredDevices, deviceStatuses, enhanceDevicesWithPriority])

  // Sort devices for flat list views (name or site)
  const sortedFlatDevices = useMemo(() => {
    if (viewMode === 'status') return []

    const enhancedDevices = enhanceDevicesWithPriority(filteredDevices)
    const sorted = [...enhancedDevices].sort((a, b) => {
      let aValue: string
      let bValue: string

      if (viewMode === 'name') {
        aValue = a.deviceName?.toLowerCase() || ''
        bValue = b.deviceName?.toLowerCase() || ''
      } else {
        aValue = a.siteName?.toLowerCase() || ''
        bValue = b.siteName?.toLowerCase() || ''
      }

      const comparison = aValue.localeCompare(bValue)
      return sortDirection === 'asc' ? comparison : -comparison
    })

    return sorted
  }, [filteredDevices, viewMode, sortDirection, enhanceDevicesWithPriority])

  // Toggle sort direction when clicking on the active column
  const handleColumnSort = (column: 'name' | 'site') => {
    if (viewMode === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setViewMode(column)
      setSortDirection('asc')
    }
  }

  // Group devices by account name for the "By Account" admin view.
  // Devices with no account name land in a single "UNASSIGNED" bucket pinned to
  // the bottom of the list so it's distinct from named accounts.
  // Within each group: priority + online devices bubble to the top; the
  // remaining order follows accountSortKey (name | site | favorites).
  const accountGroups = useMemo(() => {
    if (viewMode !== 'account') return []
    const enhanced = enhanceDevicesWithPriority(filteredDevices)
    const UNASSIGNED = 'UNASSIGNED'
    const byAccount = new Map<string, typeof enhanced>()
    for (const device of enhanced) {
      const raw = (device.accountName || '').trim()
      const key = !raw || raw.toUpperCase() === 'UNKNOWN LOCATION' ? UNASSIGNED : raw
      const list = byAccount.get(key)
      if (list) list.push(device)
      else byAccount.set(key, [device])
    }

    const statusRank = (sigfoxId: string): number => {
      const s = deviceStatuses[sigfoxId]?.status
      if (s === 'online') return 0
      if (s === 'offline') return 2
      return 1 // pending / unknown sits between online and offline
    }

    const compareDevices = (a: any, b: any): number => {
      // Priority always bubbles up.
      const aPri = a.isPriority ? 0 : 1
      const bPri = b.isPriority ? 0 : 1
      if (aPri !== bPri) return aPri - bPri

      if (accountSortKey === 'favorites') {
        // After priority, fall back to alphabetical by device name.
        return (a.deviceName?.toLowerCase() || '').localeCompare(
          b.deviceName?.toLowerCase() || ''
        )
      }

      // Online before offline before pending.
      const aStatus = statusRank(a.sigfoxId)
      const bStatus = statusRank(b.sigfoxId)
      if (aStatus !== bStatus) return aStatus - bStatus

      if (accountSortKey === 'site') {
        const aSite = (a.siteName?.toLowerCase() || '￿') // empty sites to bottom
        const bSite = (b.siteName?.toLowerCase() || '￿')
        const cmp = aSite.localeCompare(bSite)
        if (cmp !== 0) return cmp
      }

      return (a.deviceName?.toLowerCase() || '').localeCompare(
        b.deviceName?.toLowerCase() || ''
      )
    }

    const groups = Array.from(byAccount.entries()).map(([name, list]) => ({
      name,
      devices: [...list].sort(compareDevices),
    }))
    return groups.sort((a, b) => {
      if (a.name === UNASSIGNED) return 1
      if (b.name === UNASSIGNED) return -1
      return a.name.localeCompare(b.name)
    })
  }, [filteredDevices, viewMode, enhanceDevicesWithPriority, deviceStatuses, accountSortKey])

  const accountCount = useMemo(() => {
    if (!isAdminAllDevices) return 0
    const set = new Set<string>()
    for (const device of devicesArray) {
      if (device.accountName) set.add(device.accountName)
    }
    return set.size
  }, [devicesArray, isAdminAllDevices])

  const totalDevices = devicesArray.length
  const totalOnline = organizedDevices.priorityOnline.length + organizedDevices.regularOnline.length
  const totalOffline = organizedDevices.priorityOffline.length + organizedDevices.regularOffline.length
  const totalPriority = priorityDeviceIds.length

  const clearSearch = () => {
    setSearchQuery('')
  }

  const handleDeviceClick = (device: DeviceInfo) => {
    // In compare mode, toggle device selection instead of navigating
    if (compareMode) {
      toggleDeviceForComparison(device.sigfoxId)
      return
    }

    // In normal mode, clicking a device while compare mode is active exits compare
    // and selects that device
    selectDeviceLocally(device.sigfoxId)

    // Preserve existing query params (like from/to time range) and update sigfoxId
    const currentParams = new URLSearchParams(location.search)
    currentParams.set('sigfoxId', device.sigfoxId)
    const queryString = currentParams.toString()

    // If clicking on already selected device, toggle between /map and /charts
    if (device.sigfoxId === selectedDevice) {
      if (location.pathname === '/map' || location.pathname === '/') {
        navigate(`/charts?${queryString}`, { replace: true })
      } else if (location.pathname === '/charts') {
        navigate(`/map?${queryString}`, { replace: true })
      } else {
        // For other pages, just update the query parameter
        navigate(`${location.pathname}?${queryString}`, { replace: true })
      }
    } else {
      // New device selected - navigate to /map by default
      const targetPath = location.pathname === '/' ? '/map' : location.pathname
      navigate(`${targetPath}?${queryString}`, { replace: true })
    }
  }

  // Handle checkbox click in compare mode (prevents event bubbling)
  const handleCompareCheckboxClick = (e: React.MouseEvent, device: DeviceInfo) => {
    e.stopPropagation()
    toggleDeviceForComparison(device.sigfoxId)
  }

  // Check if device is selected for comparison
  const isDeviceSelectedForCompare = (sigfoxId: string) => {
    return selectedDevicesForComparison.includes(sigfoxId)
  }

  // Check if at compare device limit
  const isAtCompareLimit = selectedDevicesForComparison.length >= MAX_COMPARE_DEVICES

  // Desktop/Tablet landscape version - collapsed sidebar
  if (isCollapsed) {
    return (
      <div className="fixed top-16 left-0 h-[calc(100vh-64px)] z-20 bg-white border-r border-gray-200 shadow-lg transition-all duration-300 w-14 flex flex-col">
        <div className="p-2 border-b border-gray-100">
          <button
            onClick={onToggleCollapse}
            className="w-full px-2 py-1.5 hover:bg-gray-100 rounded transition-colors flex items-center justify-center"
            title="Expand device list"
          >
            <FontAwesomeIcon icon={faChevronRight} className="text-gray-400 text-sm" />
          </button>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center space-y-6 py-4">
          <div className="transform -rotate-90 text-xs font-medium text-gray-500 whitespace-nowrap origin-center">
            Devices
          </div>
          <div className="text-xs font-semibold text-gray-600">{totalDevices}</div>
          <div className="flex flex-col items-center space-y-4">
            <div className="flex flex-col items-center space-y-1">
              <FontAwesomeIcon icon={faStar} className="text-amber-500 text-xs" />
              <div className="text-xs font-medium text-gray-600">{totalPriority}</div>
            </div>
            <div className="flex flex-col items-center space-y-1">
              <FontAwesomeIcon icon={faCircle} className="text-green-500 text-xs" />
              <div className="text-xs font-medium text-gray-600">{totalOnline}</div>
            </div>
            <div className="flex flex-col items-center space-y-1">
              <FontAwesomeIcon icon={faCircle} className="text-gray-300 text-xs" />
              <div className="text-xs font-medium text-gray-600">{totalOffline}</div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Desktop version - expanded sidebar
  return (
    <div
      style={{ width }}
      className="fixed top-16 left-0 h-[calc(100vh-64px)] z-20 bg-white border-r border-gray-200 shadow-lg transition-all duration-300 flex flex-col"
    >
      {/* Header */}
      <div className="px-2 py-2 border-b border-gray-200 space-y-2">
        {/* Search + Collapse Button */}
        <div className="flex gap-1">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
              <FontAwesomeIcon icon={faSearch} className="text-gray-400 text-sm" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search devices..."
              className="w-full pl-8 pr-6 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
            {searchQuery && (
              <button onClick={clearSearch} className="absolute inset-y-0 right-0 pr-2 flex items-center">
                <FontAwesomeIcon icon={faTimes} className="text-gray-400 text-sm hover:text-gray-600" />
              </button>
            )}
          </div>
          <button
            onClick={onToggleCollapse}
            className="w-8 hover:bg-gray-100 rounded transition-colors flex-shrink-0 flex items-center justify-center"
            title="Collapse device list"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="text-gray-400 text-sm" />
          </button>
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600 whitespace-nowrap">
            {compareMode ? (
              <>
                <span className="font-semibold text-blue-700">{selectedDevicesForComparison.length}</span>
                <span className="text-blue-500">/{MAX_COMPARE_DEVICES}</span>
                <span> selected</span>
              </>
            ) : isAdminAllDevices && isPaginating ? (
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse" />
                {loadedCount} of ~{totalCount ?? '…'} devices
              </span>
            ) : isAdminAllDevices ? (
              <>
                {totalDevices} devices{accountCount > 0 ? ` (${accountCount} accounts)` : ''}
              </>
            ) : (
              <>{filteredDevices.length} of {totalDevices} devices</>
            )}
          </span>
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-0.5">
              <FontAwesomeIcon icon={faStar} className="text-amber-500 text-xs" />
              <span className="text-gray-600">{totalPriority}</span>
            </div>
            <div className="flex items-center space-x-0.5">
              <FontAwesomeIcon icon={faCircle} className="text-green-500 text-xs" />
              <span className="text-gray-600">{totalOnline}</span>
            </div>
            <div className="flex items-center space-x-0.5">
              <FontAwesomeIcon icon={faCircle} className="text-gray-300 text-xs" />
              <span className="text-gray-600">{totalOffline}</span>
            </div>
          </div>
        </div>

        {/* View Switcher */}
        <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
          {!isAdminAllDevices && (
            <>
              <button
                onClick={() => handleColumnSort('name')}
                className={`flex-1 px-1 py-1 text-xs font-medium rounded-md transition-all whitespace-nowrap ${
                  viewMode === 'name'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                By Name
              </button>
              <button
                onClick={() => handleColumnSort('site')}
                className={`flex-1 px-1 py-1 text-xs font-medium rounded-md transition-all whitespace-nowrap ${
                  viewMode === 'site'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                By Site
              </button>
            </>
          )}
          <button
            onClick={() => setViewMode('status')}
            className={`flex-1 px-1 py-1 text-xs font-medium rounded-md transition-all whitespace-nowrap ${
              viewMode === 'status'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            By Status
          </button>
          {isAdminAllDevices && (
            <button
              onClick={() => setViewMode('account')}
              className={`flex-1 px-1 py-1 text-xs font-medium rounded-md transition-all whitespace-nowrap ${
                viewMode === 'account'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              By Account
            </button>
          )}
        </div>

        {/* Within-group sort selector — only relevant for By Account view.
            Online + favorites always bubble to the top; this controls the
            secondary order. */}
        {viewMode === 'account' && (
          <div className="flex items-center gap-1.5 text-xs text-gray-500">
            <span className="whitespace-nowrap">Sort:</span>
            {(
              [
                { key: 'name' as const, label: 'RIC' },
                { key: 'site' as const, label: 'Site' },
                { key: 'favorites' as const, label: 'Favorites' },
              ]
            ).map((opt) => (
              <button
                key={opt.key}
                onClick={() => setAccountSortKey(opt.key)}
                className={`px-2 py-0.5 rounded-md transition-colors whitespace-nowrap ${
                  accountSortKey === opt.key
                    ? 'bg-blue-50 text-blue-700 font-medium'
                    : 'hover:bg-gray-100 text-gray-600'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Device List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mb-3"></div>
            <p className="text-sm">Loading devices...</p>
          </div>
        ) : filteredDevices.length === 0 ? (
          <EmptyState
            variant={searchQuery ? 'search' : 'default'}
            title={searchQuery ? 'No devices found' : 'No devices'}
            description={searchQuery ? 'Try adjusting your search terms' : 'No devices are currently available'}
            className="py-8"
          />
        ) : viewMode === 'account' ? (
          /* Grouped View (By Account) — admin only.
             Groups are collapsed by default; click the header to expand. */
          <div className="p-2">
            {accountGroups.map((group) => {
              const isExpanded = expandedAccounts.has(group.name)
              return (
                <div key={group.name} className="mb-3">
                  <button
                    onClick={() => toggleAccountExpanded(group.name)}
                    className="w-full flex items-center space-x-1.5 px-2 py-1 hover:bg-gray-50 rounded transition-colors text-xs font-medium text-gray-500 uppercase tracking-wide"
                    aria-expanded={isExpanded}
                  >
                    <FontAwesomeIcon
                      icon={isExpanded ? faChevronDown : faChevronRight}
                      className="text-gray-400 text-xs w-3"
                    />
                    <span>{group.name} ({group.devices.length})</span>
                  </button>
                  {isExpanded && (
                    <div>
                      {group.devices.map((device) => (
                        <DeviceRow
                          key={device.deviceId}
                          device={device}
                          isSelected={device.sigfoxId === selectedDevice}
                          onClick={() => handleDeviceClick(device)}
                          status={deviceStatuses[device.sigfoxId]?.status || 'pending'}
                          lowBattery={!!lowBatteryDevices[String(device.deviceId)]}
                          onTogglePriority={toggleDevicePriority}
                          isPriority={priorityDeviceIds.includes(device.sigfoxId)}
                          compareMode={compareMode}
                          isSelectedForCompare={isDeviceSelectedForCompare(device.sigfoxId)}
                          isAtCompareLimit={isAtCompareLimit}
                          onCompareCheckboxClick={handleCompareCheckboxClick}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        ) : viewMode === 'status' ? (
          /* Grouped View (By Status) */
          <div className="p-2">
            {/* Priority Online Devices */}
            {organizedDevices.priorityOnline.length > 0 && (
              <div className="mb-3">
                <h3 className="text-xs font-medium text-amber-600 uppercase tracking-wide px-2 mb-1 flex items-center space-x-1.5">
                  <FontAwesomeIcon icon={faStar} className="text-amber-500 text-xs" />
                  <span>Priority Online ({organizedDevices.priorityOnline.length})</span>
                </h3>
                <div>
                  {organizedDevices.priorityOnline.map((device) => (
                    <DeviceRow
                      key={device.deviceId}
                      device={device}
                      isSelected={device.sigfoxId === selectedDevice}
                      onClick={() => handleDeviceClick(device)}
                      status={deviceStatuses[device.sigfoxId]?.status || 'pending'}
                      lowBattery={!!lowBatteryDevices[String(device.deviceId)]}
                      onTogglePriority={toggleDevicePriority}
                      isPriority={true}
                      compareMode={compareMode}
                      isSelectedForCompare={isDeviceSelectedForCompare(device.sigfoxId)}
                      isAtCompareLimit={isAtCompareLimit}
                      onCompareCheckboxClick={handleCompareCheckboxClick}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Priority Offline Devices */}
            {organizedDevices.priorityOffline.length > 0 && (
              <div className="mb-3">
                <h3 className="text-xs font-medium text-amber-600 uppercase tracking-wide px-2 mb-1 flex items-center space-x-1.5">
                  <FontAwesomeIcon icon={faStar} className="text-amber-500 text-xs" />
                  <span>Priority Offline ({organizedDevices.priorityOffline.length})</span>
                </h3>
                <div>
                  {organizedDevices.priorityOffline.map((device) => (
                    <DeviceRow
                      key={device.deviceId}
                      device={device}
                      isSelected={device.sigfoxId === selectedDevice}
                      onClick={() => handleDeviceClick(device)}
                      status={deviceStatuses[device.sigfoxId]?.status || 'pending'}
                      lowBattery={!!lowBatteryDevices[String(device.deviceId)]}
                      onTogglePriority={toggleDevicePriority}
                      isPriority={true}
                      compareMode={compareMode}
                      isSelectedForCompare={isDeviceSelectedForCompare(device.sigfoxId)}
                      isAtCompareLimit={isAtCompareLimit}
                      onCompareCheckboxClick={handleCompareCheckboxClick}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Online Devices */}
            {organizedDevices.regularOnline.length > 0 && (
              <div className="mb-3">
                <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide px-2 mb-1">
                  Online ({organizedDevices.regularOnline.length})
                </h3>
                <div>
                  {organizedDevices.regularOnline.map((device) => (
                    <DeviceRow
                      key={device.deviceId}
                      device={device}
                      isSelected={device.sigfoxId === selectedDevice}
                      onClick={() => handleDeviceClick(device)}
                      status={deviceStatuses[device.sigfoxId]?.status || 'pending'}
                      lowBattery={!!lowBatteryDevices[String(device.deviceId)]}
                      onTogglePriority={toggleDevicePriority}
                      isPriority={false}
                      compareMode={compareMode}
                      isSelectedForCompare={isDeviceSelectedForCompare(device.sigfoxId)}
                      isAtCompareLimit={isAtCompareLimit}
                      onCompareCheckboxClick={handleCompareCheckboxClick}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Offline Devices */}
            {organizedDevices.regularOffline.length > 0 && (
              <div>
                <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide px-2 mb-1">
                  Offline ({organizedDevices.regularOffline.length})
                </h3>
                <div>
                  {organizedDevices.regularOffline.map((device) => (
                    <DeviceRow
                      key={device.deviceId}
                      device={device}
                      isSelected={device.sigfoxId === selectedDevice}
                      onClick={() => handleDeviceClick(device)}
                      status={deviceStatuses[device.sigfoxId]?.status || 'pending'}
                      lowBattery={!!lowBatteryDevices[String(device.deviceId)]}
                      onTogglePriority={toggleDevicePriority}
                      isPriority={false}
                      compareMode={compareMode}
                      isSelectedForCompare={isDeviceSelectedForCompare(device.sigfoxId)}
                      isAtCompareLimit={isAtCompareLimit}
                      onCompareCheckboxClick={handleCompareCheckboxClick}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Flat Sorted View (By Name or By Site) */
          <div>
            {/* Column Headers */}
            <div className="sticky top-0 bg-gray-50 border-b border-gray-200 px-2 py-2 flex items-center text-xs font-medium text-gray-500 uppercase tracking-wide">
              {compareMode && <div className="w-8 flex-shrink-0" />} {/* Checkbox spacer */}
              <div className="w-5 flex-shrink-0" /> {/* Status dot spacer */}
              <button
                onClick={() => handleColumnSort('name')}
                className={`w-[110px] flex-shrink-0 flex items-center space-x-1 hover:text-gray-700 ${viewMode === 'name' ? 'text-gray-900' : ''}`}
              >
                <span>Name</span>
                {viewMode === 'name' && (
                  <FontAwesomeIcon
                    icon={sortDirection === 'asc' ? faChevronUp : faChevronDown}
                    className="text-xs"
                  />
                )}
              </button>
              <button
                onClick={() => handleColumnSort('site')}
                className={`flex-1 flex items-center space-x-1 hover:text-gray-700 ${viewMode === 'site' ? 'text-gray-900' : ''}`}
              >
                <span>Site</span>
                {viewMode === 'site' && (
                  <FontAwesomeIcon
                    icon={sortDirection === 'asc' ? faChevronUp : faChevronDown}
                    className="text-xs"
                  />
                )}
              </button>
              {!compareMode && <div className="w-7 flex-shrink-0" />} {/* Star spacer */}
            </div>

            {/* Flat Device List */}
            <div className="p-2">
              {sortedFlatDevices.map((device) => (
                <DeviceRow
                  key={device.deviceId}
                  device={device}
                  isSelected={device.sigfoxId === selectedDevice}
                  onClick={() => handleDeviceClick(device)}
                  status={deviceStatuses[device.sigfoxId]?.status || 'pending'}
                  onTogglePriority={toggleDevicePriority}
                  isPriority={priorityDeviceIds.includes(device.sigfoxId)}
                  compareMode={compareMode}
                  isSelectedForCompare={isDeviceSelectedForCompare(device.sigfoxId)}
                  isAtCompareLimit={isAtCompareLimit}
                  onCompareCheckboxClick={handleCompareCheckboxClick}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Compare Mode switch — pinned to the bottom of the device list */}
      {showCompareToggle && (
        <div className="border-t border-gray-200 p-2">
          <div
            className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 transition-colors ${
              compareMode ? 'bg-purple-50 border-purple-200' : 'bg-gray-50 border-gray-200'
            }`}
          >
            <FontAwesomeIcon
              icon={faLayerGroup}
              className={`text-sm shrink-0 ${compareMode ? 'text-purple-600' : 'text-gray-400'}`}
            />
            <Switch
              label="Compare devices"
              size="sm"
              state={compareMode}
              onChange={handleToggleCompareMode}
              labelClassName={compareMode ? 'text-purple-700 font-medium' : 'text-gray-700'}
            />
          </div>
        </div>
      )}

      {/* Resize drag handle */}
      <div
        onMouseDown={handleResizeMouseDown}
        className={`absolute top-0 right-0 w-1 h-full cursor-col-resize transition-colors ${
          isResizing ? 'bg-blue-500' : 'bg-transparent hover:bg-blue-300'
        }`}
      />
    </div>
  )
}

interface DeviceRowProps {
  device: DeviceInfo
  isSelected: boolean
  onClick: () => void
  status: DeviceStatus
  lowBattery?: boolean
  onTogglePriority: (deviceId: string, isPriority: boolean) => Promise<void>
  isPriority: boolean
  // Compare mode props
  compareMode?: boolean
  isSelectedForCompare?: boolean
  isAtCompareLimit?: boolean
  onCompareCheckboxClick?: (e: React.MouseEvent, device: DeviceInfo) => void
}

/**
 * Compact device row component (40px height)
 * Layout: [checkbox?] [status dot] [device name] [site name] [favorite star]
 */
function DeviceRow({
  device,
  isSelected,
  onClick,
  status,
  lowBattery,
  onTogglePriority,
  isPriority,
  compareMode = false,
  isSelectedForCompare = false,
  isAtCompareLimit = false,
  onCompareCheckboxClick,
}: DeviceRowProps) {
  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation()
    await onTogglePriority(device.sigfoxId, !isPriority)
  }

  // Determine if this row can be selected in compare mode
  const canSelect = !isAtCompareLimit || isSelectedForCompare

  // Determine row styling based on mode and selection state
  const getRowClassName = () => {
    const baseClasses = 'w-full h-10 px-2 flex items-center cursor-pointer transition-colors rounded'

    if (compareMode) {
      if (isSelectedForCompare) {
        return `${baseClasses} bg-blue-50 border-l-4 border-blue-500`
      }
      if (!canSelect) {
        return `${baseClasses} opacity-50 cursor-not-allowed bg-gray-50`
      }
      return `${baseClasses} hover:bg-blue-50/50`
    }

    // Normal mode styling
    if (isSelected) {
      return `${baseClasses} ${isPriority ? 'bg-amber-100' : 'bg-blue-50'}`
    }
    return `${baseClasses} ${isPriority ? 'bg-amber-50/50 hover:bg-amber-50' : 'hover:bg-gray-50'}`
  }

  return (
    <div
      onClick={canSelect ? onClick : undefined}
      role="button"
      tabIndex={canSelect ? 0 : -1}
      onKeyDown={(e) => {
        if (canSelect && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          onClick()
        }
      }}
      className={getRowClassName()}
      title={!canSelect && compareMode ? 'Maximum 10 devices selected' : undefined}
    >
      {/* Compare mode checkbox */}
      {compareMode && (
        <button
          onClick={(e) => canSelect && onCompareCheckboxClick?.(e, device)}
          className={`w-7 h-7 flex-shrink-0 flex items-center justify-center mr-1 rounded transition-colors ${
            canSelect ? 'hover:bg-blue-100' : 'cursor-not-allowed'
          }`}
          aria-label={isSelectedForCompare ? 'Remove from comparison' : 'Add to comparison'}
          disabled={!canSelect}
        >
          {isSelectedForCompare ? (
            <FontAwesomeIcon
              icon={faCheckSquare}
              className="text-base text-blue-600"
            />
          ) : (
            <span
              className={`w-4 h-4 border-2 rounded-sm ${
                canSelect
                  ? 'border-gray-400 bg-white hover:border-blue-400'
                  : 'border-gray-300 bg-gray-100'
              }`}
            />
          )}
        </button>
      )}

      {/* Status dot */}
      <div className="w-5 flex-shrink-0 flex items-center justify-center">
        <DeviceStatusIcon status={status} lowBattery={lowBattery} />
      </div>

      {/* Device name - fixed width since format is always RIC1030_XXXX */}
      <div className="w-[110px] flex-shrink-0 pr-2">
        <span
          className="text-sm font-medium text-gray-900 truncate block"
          title={device.deviceName || 'Unnamed'}
        >
          {device.deviceName || 'Unnamed'}
        </span>
      </div>

      {/* Site name - takes remaining space */}
      <div className="flex-1 min-w-0 pr-2">
        <span
          className="text-sm text-gray-500 truncate block"
          title={device.siteName || '-'}
        >
          {device.siteName || '-'}
        </span>
      </div>

      {/* Favorite star - hidden in compare mode */}
      {!compareMode && (
        <button
          onClick={handleFavoriteClick}
          className="w-7 h-7 flex-shrink-0 flex items-center justify-center hover:bg-gray-100 rounded transition-colors"
          aria-label={isPriority ? 'Remove from favorites' : 'Add to favorites'}
        >
          <FontAwesomeIcon
            icon={faStar}
            className={`text-xs ${isPriority ? 'text-amber-500' : 'text-gray-300 hover:text-amber-400'}`}
          />
        </button>
      )}
    </div>
  )
}

